// nearMissPatchQueue.js
// IndexedDB queue management for Near Miss form autosave and holding websocket requests for offline capabilities

import { openDB } from "idb";
import { withIdbRetry } from "./idbRetry";

const DB_NAME = "nearMissPatchQueue";
const STORE_NAME = "patches";

const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "queueId",
          autoIncrement: true,
        });
        store.createIndex("nearMissId", "nearMissId", { unique: false });
      }
    },
  });
};

export const queuePatch = async (nearMissId, patch) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      return db.add(STORE_NAME, {
        nearMissId,
        patch,
        timestamp: Date.now(),
      });
    },
    undefined,
    "nearMissPatchQueue.queuePatch"
  );
};

export const getQueuedPatches = async (nearMissId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.store.index("nearMissId");
      return index.getAll(nearMissId);
    },
    [],
    "nearMissPatchQueue.getQueuedPatches"
  );
};

export const removePatchFromQueue = async (queueId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      await db.delete(STORE_NAME, queueId);
    },
    undefined,
    "nearMissPatchQueue.removePatchFromQueue"
  );
};

export const getPendingCount = async (nearMissId) => {
  const patches = await getQueuedPatches(nearMissId);
  return patches.length;
};

export const flushQueue = async (nearMissId, emitFn) => {
  const patches = await getQueuedPatches(nearMissId);
  let successCount = 0;
  for (const item of patches) {
    const result = await emitFn(nearMissId, item.patch);
    // Remove from queue if: success OR server rejected it (validation error)
    // Only keep in queue if it should be retried (network issues)
    if (!result.error || !result.shouldQueue) {
      await removePatchFromQueue(item.queueId);
      if (!result.error) {
        successCount++;
      }
    }
  }
  return successCount;
};

export const clearQueue = async (nearMissId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const index = tx.store.index("nearMissId");
      const keys = await index.getAllKeys(nearMissId);
      for (const key of keys) {
        await tx.store.delete(key);
      }
      await tx.done;
    },
    undefined,
    "nearMissPatchQueue.clearQueue"
  );
};
